import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../Shared/Services/api.service';
import { endpoints } from '../../../../environments/endpoints';
import { LaravelDefaultResponse } from '../../../Interfaces/api/shared/response.interface';
import { TypedResourceRequest } from '../../Shared/Interfaces/api-request.interface';
import {
  CreditNoteCreatedResponse,
  CreditNoteListResponse,
  CreditNotePayload,
  InvoiceListResponse,
  InvoicePdfResponse,
  InvoiceShowResponse,
  PaymentAttentionResponse,
  PaymentAttentionStateResponse,
  PaymentListResponse,
  PaymentShowResponse,
  RefundCreatedResponse,
  RefundListResponse,
  RefundPayload,
} from '../../../Interfaces/api/backoffice/billing-document.interface';
import { CreditNote } from '../../../Models/backoffice/credit-note.model';
import { InvoiceDetail } from '../../../Models/backoffice/invoice-detail.model';
import { InvoiceSummary } from '../../../Models/backoffice/invoice-summary.model';
import { PaymentSummary } from '../../../Models/backoffice/payment-summary.model';
import { Refund } from '../../../Models/backoffice/refund.model';
import { PagedResult } from '../../Shared/Interfaces/paged-result.interface';
import { InvoiceFilters } from '../Interfaces/billing-dialog.interface';

@Injectable({
  providedIn: 'root',
})
export class BillingDocumentService {
  private readonly _apiService = inject(ApiService);

  invoicesRequest(
    organizationId: string,
    filters: InvoiceFilters,
    page: number,
    pageSize: number,
  ): TypedResourceRequest<LaravelDefaultResponse<InvoiceListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<InvoiceListResponse>>(
      endpoints.backoffice.organizationInvoices(organizationId),
      { params: this._params(filters, page, pageSize), skipLoading: true, skipError: true },
    );
  }

  invoiceRequest(
    organizationId: string,
    invoiceId: string,
  ): TypedResourceRequest<LaravelDefaultResponse<InvoiceShowResponse>> {
    return this._apiService.get<LaravelDefaultResponse<InvoiceShowResponse>>(
      endpoints.backoffice.organizationInvoice(organizationId, invoiceId),
      { skipLoading: true, skipError: true },
    );
  }

  paymentsRequest(
    organizationId: string,
    status: string,
    page: number,
    pageSize: number,
  ): TypedResourceRequest<LaravelDefaultResponse<PaymentListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<PaymentListResponse>>(
      endpoints.backoffice.organizationPayments(organizationId),
      {
        params: this._params({ stripe_status: status }, page, pageSize),
        skipLoading: true,
        skipError: true,
      },
    );
  }

  attentionRequest(
    organizationId: string,
  ): TypedResourceRequest<LaravelDefaultResponse<PaymentAttentionResponse>> {
    return this._apiService.get<LaravelDefaultResponse<PaymentAttentionResponse>>(
      endpoints.backoffice.organizationPaymentsAttention(organizationId),
      { skipLoading: true, skipError: true },
    );
  }

  refundsRequest(
    organizationId: string,
    page: number,
    pageSize: number,
  ): TypedResourceRequest<LaravelDefaultResponse<RefundListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<RefundListResponse>>(
      endpoints.backoffice.organizationRefunds(organizationId),
      { params: { page, pageSize }, skipLoading: true, skipError: true },
    );
  }

  creditNotesRequest(
    organizationId: string,
    page: number,
    pageSize: number,
  ): TypedResourceRequest<LaravelDefaultResponse<CreditNoteListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<CreditNoteListResponse>>(
      endpoints.backoffice.organizationCreditNotes(organizationId),
      { params: { page, pageSize }, skipLoading: true, skipError: true },
    );
  }

  documentUrl(organizationId: string, invoiceId: string): Observable<string> {
    const request = this._apiService.get<LaravelDefaultResponse<InvoicePdfResponse>>(
      endpoints.backoffice.organizationInvoicePdf(organizationId, invoiceId),
      { skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<InvoicePdfResponse>>(request)
      .pipe(map((response) => response.data.url));
  }

  resend(organizationId: string, invoiceId: string, reason: string): Observable<InvoiceDetail> {
    return this._invoiceMutation(
      endpoints.backoffice.organizationInvoiceResend(organizationId, invoiceId),
      { reason },
    );
  }

  markUncollectible(
    organizationId: string,
    invoiceId: string,
    reason: string,
  ): Observable<InvoiceDetail> {
    return this._invoiceMutation(
      endpoints.backoffice.organizationInvoiceUncollectible(organizationId, invoiceId),
      { reason },
    );
  }

  retryPayment(
    organizationId: string,
    paymentId: string,
    reason: string,
  ): Observable<PaymentSummary> {
    const request = this._apiService.post<LaravelDefaultResponse<PaymentShowResponse>>(
      endpoints.backoffice.organizationPaymentRetry(organizationId, paymentId),
      { body: { reason }, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<PaymentShowResponse>>(request)
      .pipe(map((response) => new PaymentSummary(response.data.payment)));
  }

  refundPayment(
    organizationId: string,
    paymentId: string,
    payload: RefundPayload,
  ): Observable<RefundCreatedResponse> {
    const request = this._apiService.post<LaravelDefaultResponse<RefundCreatedResponse>>(
      endpoints.backoffice.organizationPaymentRefund(organizationId, paymentId),
      { body: payload, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<RefundCreatedResponse>>(request)
      .pipe(map((response) => response.data));
  }

  issueCreditNote(
    organizationId: string,
    invoiceId: string,
    payload: CreditNotePayload,
  ): Observable<CreditNoteCreatedResponse> {
    const request = this._apiService.post<LaravelDefaultResponse<CreditNoteCreatedResponse>>(
      endpoints.backoffice.organizationInvoiceCreditNote(organizationId, invoiceId),
      { body: payload, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<CreditNoteCreatedResponse>>(request)
      .pipe(map((response) => response.data));
  }

  toInvoices(
    response: LaravelDefaultResponse<InvoiceListResponse> | undefined,
  ): PagedResult<InvoiceSummary> {
    return {
      items: (response?.data?.invoices ?? []).map((row) => new InvoiceSummary(row)),
      total: response?.data?.total ?? 0,
      page: response?.data?.page ?? 0,
      pageSize: response?.data?.pageSize ?? 0,
    };
  }

  toInvoiceDetail(
    response: LaravelDefaultResponse<InvoiceShowResponse> | undefined,
  ): InvoiceDetail | null {
    const row = response?.data?.invoice;

    return row ? new InvoiceDetail(row) : null;
  }

  toPayments(
    response: LaravelDefaultResponse<PaymentListResponse> | undefined,
  ): PagedResult<PaymentSummary> {
    return {
      items: (response?.data?.payments ?? []).map((row) => new PaymentSummary(row)),
      total: response?.data?.total ?? 0,
      page: response?.data?.page ?? 0,
      pageSize: response?.data?.pageSize ?? 0,
    };
  }

  toAttention(
    response: LaravelDefaultResponse<PaymentAttentionResponse> | undefined,
  ): PaymentAttentionStateResponse | null {
    return response?.data?.attention ?? null;
  }

  toRefunds(
    response: LaravelDefaultResponse<RefundListResponse> | undefined,
  ): PagedResult<Refund> {
    return {
      items: (response?.data?.items ?? []).map((row) => new Refund(row)),
      total: response?.data?.total ?? 0,
      page: response?.data?.page ?? 0,
      pageSize: response?.data?.pageSize ?? 0,
    };
  }

  toCreditNotes(
    response: LaravelDefaultResponse<CreditNoteListResponse> | undefined,
  ): PagedResult<CreditNote> {
    return {
      items: (response?.data?.items ?? []).map((row) => new CreditNote(row)),
      total: response?.data?.total ?? 0,
      page: response?.data?.page ?? 0,
      pageSize: response?.data?.pageSize ?? 0,
    };
  }

  private _invoiceMutation(
    resource: string,
    body: Record<string, unknown>,
  ): Observable<InvoiceDetail> {
    const request = this._apiService.post<LaravelDefaultResponse<InvoiceShowResponse>>(resource, {
      body,
      skipError: true,
    });

    return this._apiService
      .run<LaravelDefaultResponse<InvoiceShowResponse>>(request)
      .pipe(map((response) => new InvoiceDetail(response.data.invoice)));
  }

  private _params(
    filters: Partial<InvoiceFilters>,
    page: number,
    pageSize: number,
  ): Record<string, string | number> {
    const params: Record<string, string | number> = { page, pageSize };

    for (const [key, value] of Object.entries(filters)) {
      if (value !== '' && value !== undefined) {
        params[key] = String(value);
      }
    }

    return params;
  }
}
